import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Button,
  ErrorState,
  PageContainer,
  Pagination,
  SurfaceCard,
  Table,
} from '@react-apps/design-system';
import type { ColumnDef } from '@react-apps/design-system';
import type { ContentItemListEntry } from '../../domain/entities/content.entity';
import { useInstitutionsById } from '../hooks/use-institutions';
import { useExamFilterOptions, useExamSearch } from '../hooks/use-exam-search';
import {
  readFiltersParams,
  writeFiltersParams,
} from '@/shared/hooks/use-url-filters';
import {
  readPageParam,
  writePageParam,
} from '@/shared/hooks/use-page-query-param';
import { readExamBody } from '../exam-body';
import {
  dropInvalid,
  EMPTY_EXAM_FILTERS,
  hasExamFilter,
  sameFilters,
  type ExamSearchFilters,
} from '../exam-search-filters';
import { ExamFilters } from '../components/ExamFilters';

const PER_PAGE = 10;

/**
 * Buscar provas (DESEN-41): filtros em cadeia (UF → Instituição → Ano →
 * Caderno) e a lista das provas que atendem a todos. Só prova real publicada:
 * rascunho não aparece em lugar nenhum.
 *
 * - **Filtros e página vivem na URL**, como no painel de conteúdos: abrir uma
 *   prova e voltar devolve a mesma busca. Mudar um filtro volta à página 1.
 * - **Parâmetro inválido na URL sai em silêncio** (`dropInvalid`): UF fora da
 *   lista e ano que não é inteiro já na leitura; instituição apagada ou de
 *   outra UF, e ano ou caderno fora das opções, quando a resposta chega.
 * - A lista atualiza a cada filtro, sem botão "Buscar"; a contagem é anunciada
 *   (`aria-live`).
 * - O clique abre a ficha da prova (`/content/items/:id`), levando a busca no
 *   `state` para o "Voltar" da ficha voltar a ela.
 */
export function ExamSearchPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const institutions = useInstitutionsById();

  const fromUrl = useMemo(
    () => readFiltersParams(searchParams, EMPTY_EXAM_FILTERS),
    [searchParams],
  );
  const page = readPageParam(searchParams);

  // As opções dependem da instituição já conferida; o ano e o caderno se
  // conferem contra elas.
  const preliminary = dropInvalid(fromUrl, {
    institutionsById: institutions.data,
  });
  const options = useExamFilterOptions(
    preliminary.institutionId,
    preliminary.year,
  );
  const filters = dropInvalid(preliminary, {
    institutionsById: institutions.data,
    options: options.data,
  });

  const update = (next: ExamSearchFilters, nextPage = 1) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        writeFiltersParams(params, next, EMPTY_EXAM_FILTERS);
        writePageParam(params, nextPage);
        return params;
      },
      { replace: true },
    );
  };

  // O que a URL trazia de inválido sai dela também, para o "Voltar" da prova
  // não devolver um filtro que a tela já descartou.
  useEffect(() => {
    if (!sameFilters(filters, fromUrl)) update(filters, page);
    // `update` muda a cada render; o gatilho é a diferença.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, fromUrl]);

  // A busca espera o que ainda está sendo conferido: a instituição (pela
  // lista) e o ano ou caderno (pelas opções). Sem isso, filtro inválido da
  // URL chegaria ao back antes de sair.
  const settled =
    (!fromUrl.institutionId || institutions.isFetched) &&
    (!filters.institutionId ||
      (!fromUrl.year && !fromUrl.variant) ||
      options.isFetched);
  const search = useExamSearch({
    filters,
    page,
    perPage: PER_PAGE,
    sortDirection: 'desc',
    enabled: settled && sameFilters(filters, fromUrl),
  });
  const filtered = hasExamFilter(filters);
  const total = search.data?.total ?? 0;

  const columns: ColumnDef<ContentItemListEntry>[] = [
    {
      key: 'exam',
      header: 'Prova',
      render: (row) => {
        const institutionId = readExamBody(row).institutionId;
        const institution = institutionId
          ? institutions.data?.get(institutionId)
          : undefined;
        return (
          <div>
            <p className="text-(--color-text-primary) text-sm font-medium">
              {row.label ?? row.id}
            </p>
            {institution && (
              <p className="text-(--color-text-secondary) text-xs">
                {institution.name}
              </p>
            )}
          </div>
        );
      },
    },
    {
      key: 'year',
      header: 'Ano',
      render: (row) => readExamBody(row).year ?? '—',
    },
    {
      key: 'variant',
      header: 'Caderno',
      render: (row) => readExamBody(row).variant || '—',
    },
  ];

  return (
    <PageContainer maxWidth="6xl">
      <h1 className="text-(--color-text-primary) text-xl font-semibold">
        Buscar provas
      </h1>

      <SurfaceCard className="space-y-5">
        <div className="space-y-3">
          <ExamFilters value={filters} onChange={(next) => update(next)} />
          <div className="flex justify-end">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={!filtered}
              onClick={() => update(EMPTY_EXAM_FILTERS)}
            >
              Limpar filtros
            </Button>
          </div>
        </div>

        <p className="text-(--color-text-secondary) text-sm" aria-live="polite">
          {search.isSuccess
            ? `${total} ${total === 1 ? 'prova encontrada' : 'provas encontradas'}`
            : ''}
        </p>

        {search.isError ? (
          <ErrorState
            message="Não foi possível carregar as provas."
            onRetry={() => void search.refetch()}
          />
        ) : (
          <>
            <Table
              columns={columns}
              data={search.data?.items ?? []}
              keyExtractor={(row) => row.id}
              loading={search.isLoading}
              emptyMessage={
                filtered
                  ? 'Nenhuma prova encontrada com esses filtros. Remova um filtro ou use Limpar filtros.'
                  : 'Ainda não há provas cadastradas.'
              }
              striped
              onRowClick={(row) =>
                void navigate(`/content/items/${row.id}`, {
                  state: { from: `${location.pathname}${location.search}` },
                })
              }
            />
            <Pagination
              page={page}
              total={total}
              perPage={PER_PAGE}
              loading={search.isLoading}
              label="prova"
              onPageChange={(next) => update(filters, next)}
            />
          </>
        )}
      </SurfaceCard>
    </PageContainer>
  );
}
